import { LoadingSpinner } from "components/LoadingSpinner";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { useMemo, useState } from "react";
import { useGetDirectPayLinksByAccountQuery } from "store/queries/directPayLinks";
import styled from "styled-components";
import { IDirectPayLink } from "types/DirectPayLink.d";

import { DirectPayLinkForm } from "./DirectPayLinkForm";
import { DirectPayLinkTable } from "./DirectPayLinkTable";

const Title = styled.h3`
  margin-bottom: 16px;
`;

const InfoSection = styled.div`
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 16px;
  margin-top: 24px;
  font-size: 14px;
  line-height: 1.4;
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 20px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const InfoColumn = styled.div`
  h4 {
    margin: 0 0 8px 0;
    font-size: 14px;
    font-weight: 600;
    color: #495057;
  }

  ul {
    margin: 0;
    padding-left: 16px;

    li {
      margin-bottom: 4px;
      color: #6c757d;
    }
  }
`;

interface IDirectPayLinksProps {
  accountId: string;
}

export function DirectPayLinks({ accountId }: IDirectPayLinksProps) {
  const { data: linksData, isLoading } =
    useGetDirectPayLinksByAccountQuery(accountId);
  const links = [...(linksData?.data || [])].sort((a, b) => {
    if (a.isMain && !b.isMain) return -1;
    if (!a.isMain && b.isMain) return 1;
    return 0;
  });

  const accountHasMainLink = useMemo(
    () => links.some((link) => link.isMain),
    [links]
  );

  const [showDialog, setShowDialog] = useState(false);
  const [editingLink, setEditingLink] = useState<IDirectPayLink | null>(null);

  const handleCreate = () => {
    setEditingLink(null);
    setShowDialog(true);
  };

  const handleEdit = (link: IDirectPayLink) => {
    setEditingLink(link);
    setShowDialog(true);
  };

  const handleSuccess = () => {
    setShowDialog(false);
    setEditingLink(null);
  };

  const handleCancel = () => {
    setShowDialog(false);
    setEditingLink(null);
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="block bg-white border-round p-4">
      <div className="flex justify-content-between align-items-center mb-3">
        <Title>Direct Pay Links</Title>
        <Button
          label="Create Link"
          icon="pi pi-plus"
          onClick={handleCreate}
          className="p-button-success"
        />
      </div>

      <p className="text-gray-600 mb-3">
        Create short links for easy patient payment. Each link can have
        customized settings for CPT code, amount, and form fields. Links can be
        set to expire by date or after a maximum number of orders.
      </p>

      <DirectPayLinkTable links={links} onEdit={handleEdit} />

      <InfoSection>
        <InfoGrid>
          <InfoColumn>
            <h4>🔒 Multi-Layer Security</h4>
            <ul>
              <li>reCAPTCHA Enterprise verification</li>
              <li>Limits: 500 per hour per link, 5000 per hour global</li>
              <li>Abuse Tracking: Auto-blocks after 100 orders in 5 minutes</li>
              <li>Real-time monitoring & alerts in Sentry</li>
            </ul>
          </InfoColumn>
          <InfoColumn>
            <h4>📋 Flexible Limits</h4>
            <ul>
              <li>Initial: 1-5,000,000 orders, no expiration date</li>
              <li>Extensions: +5,000,000 remaining orders each time</li>
              <li>Expires when order capacity is reached</li>
            </ul>
          </InfoColumn>

          <InfoColumn>
            <h4>⚠️ Strict Requirements</h4>
            <ul>
              <li>Hospital must have Direct Pay enabled</li>
              <li>Hospital status must be Active</li>
              <li>Cannot decrease orders below created count</li>
              <li>Auto-blocked links need manual reactivation</li>
            </ul>
          </InfoColumn>
        </InfoGrid>
      </InfoSection>

      <Dialog
        header={
          editingLink?.id ? "Edit Direct Pay Link" : "Create Direct Pay Link"
        }
        visible={showDialog}
        style={{ width: "800px" }}
        onHide={handleCancel}
        draggable={false}
        modal
        blockScroll
      >
        <DirectPayLinkForm
          accountId={accountId}
          editingLink={editingLink}
          onSuccess={handleSuccess}
          onCancel={handleCancel}
          accountHasMainLink={accountHasMainLink}
        />
      </Dialog>
    </div>
  );
}
